import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { IonDatetime } from '@ionic/angular';
import { PlanColoringController } from 'src/controllers/plan-coloring.controller';
import { Mesocycle } from 'src/models/mesocycle.model';
import { PlanHistory } from 'src/models/plan.model';
import { MesocycleColorService } from 'src/services/mesocycle-color.service';
import { PlanService } from 'src/services/plan.service';

@Component({
  selector: 'app-calendar-history',
  template: `
    <ion-datetime
      #datetime
      presentation="date"
      [highlightedDates]="highlightedDates"
      (ionChange)="onDateChange($event)"
    ></ion-datetime>
  `,
  styleUrls: ['./calendar-history.component.scss'],
  standalone: false,
})
export class CalendarHistoryComponent implements OnInit, OnChanges {
  @ViewChild('datetime') datetime!: IonDatetime;
  @Input() planHistories: PlanHistory[] = [];
  @Input() mesocycles: Mesocycle[] = [];
  highlightedDates: {
    date: string;
    textColor: string;
    backgroundColor: string;
  }[] = [];

  constructor(
    private planService: PlanService,
    private router: Router,
    private planColoring: PlanColoringController,
    private mesocycleColors: MesocycleColorService
  ) {}

  ngOnInit() {
    this.loadWorkoutDays();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['planHistories'] || changes['mesocycles']) {
      this.setHighlightedDates();
    }
  }

  async loadWorkoutDays() {
    try {
      if (this.planHistories.length === 0) {
        this.planHistories = await this.planService.getUserPlanHistories();
      }
      this.setHighlightedDates();
    } catch (error) {
      console.error('Error loading workout days:', error);
    }
  }

  /** Legacy histories retain the plan colour; linked histories take their cycle colour. */
  private setHighlightedDates(): void {
    const cycles = new Map<string, Mesocycle>(
      this.mesocycles
        .filter((cycle) => !!cycle.id)
        .map((cycle): [string, Mesocycle] => [cycle.id!, cycle])
    );
    this.highlightedDates = this.planHistories.map((history) => {
      const cycle = history.mesocycleId
        ? cycles.get(history.mesocycleId)
        : undefined;
      const backgroundColor = cycle?.color || this.planColoring.stringToColor(
        history.activePlanId
      );
      return {
        date: this.formatDate(history.startTime.toDate()),
        textColor: cycle
          ? this.mesocycleColors.textColorFor(cycle.color)
          : this.planColoring.getContrastYIQ(backgroundColor),
        backgroundColor,
      };
    });
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onDateChange(event: CustomEvent) {
    const selectedDate = new Date(event.detail.value);
    const formattedSelectedDate = this.formatDate(selectedDate);

    const selectedHistory = this.planHistories.find(
      (history) =>
        this.formatDate(history.startTime.toDate()) === formattedSelectedDate
    );

    if (selectedHistory) {
      this.openDetail(selectedHistory);
    }
  }

  openDetail(history: PlanHistory) {
    this.router.navigate(['home/workout-history', history.id]);
  }
}
